import React, { useState, useEffect, useCallback } from "react";
import {
  Container,
  Header,
  Button,
  Box,
  SpaceBetween,
} from "@cloudscape-design/components";
import { useTranslation } from "react-i18next";

const TutorialGuide = ({
  steps = [],
  storageKey = "tutorial_seen",
  autoStart = true,
  onComplete,
  triggerButton = true,
  forceShow = false,
}) => {
  const { t } = useTranslation();
  const [showTutorial, setShowTutorial] = useState(() => {
    if (forceShow) return true;

    if (!autoStart) return false;

    return !localStorage.getItem(storageKey);
  });
  const [currentStep, setCurrentStep] = useState(0);
  const [highlightedElement, setHighlightedElement] = useState(null);
  const [modalPosition, setModalPosition] = useState({
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)"
  });

  useEffect(() => {
    if (forceShow && !showTutorial) {
      setShowTutorial(true);
      setCurrentStep(0);
    }
  }, [forceShow, showTutorial]);

  const highlightElement = useCallback((selector, position = "bottom") => {
    if (!selector) {
      setHighlightedElement(null);
      setModalPosition({
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
      });
      document.querySelectorAll(".tutorial-highlight").forEach((el) => {
        el.classList.remove("tutorial-highlight");
      });
      return;
    }

    const findElement = (selectors) => {
      if (typeof selectors === "string") {
        selectors = [selectors];
      }

      for (const sel of selectors) {
        const element = document.querySelector(sel);
        if (element) return element;
      }
      return null;
    };

    let possibleSelectors = [selector];

    if (selector.includes("upload-section")) {
      possibleSelectors = [
        "#upload-section",
        '[data-testid="upload-section"]',
        ".upload-section",
        'form[action*="upload"]',
        '[role="form"]',
        ".awsui_form_container",
      ];
    } else if (selector.includes("audio-status-section")) {
      possibleSelectors = [
        "#audio-status-section",
        '[data-testid="audio-status-section"]',
        ".audio-status-section",
        '[data-testid*="progress"]',
        ".awsui_progress-bar",
      ];
    } else if (selector.includes("collection-preferences")) {
      possibleSelectors = [
        '[data-testid="collection-preferences-trigger"]',
        ".awsui_collection-preferences-trigger",
        '[aria-label*="Preferences"]',
        '[aria-label*="preferencias"]',
        'button[aria-haspopup="dialog"]',
        ".awsui_preferences-trigger",
      ];
    } else if (selector.includes("property-filter")) {
      possibleSelectors = [
        '[data-testid="property-filter"]',
        ".awsui_property-filter",
        '[placeholder*="Search"]',
        '[placeholder*="Buscar"]',
        '[placeholder*="filtrar"]',
        'input[type="text"]',
      ];
    } else if (selector.includes("dashboard-metrics")) {
      possibleSelectors = [
        "#dashboard-metrics-section",
        '[data-testid="dashboard-metrics"]',
        ".dashboard-metrics",
        '[class*="dashboard"]',
        '[class*="metrics"]',
      ];
    } else if (selector.includes("search-button")) {
      possibleSelectors = [
        '[data-testid="header-search-button"]',
        'button[href="search"]',
        '.awsui_button[href="search"]',
        'a[href="search"]',
        '[aria-label*="search"]',
        '[title*="search"]',
      ];
    } else if (selector.includes("insights-button")) {
      possibleSelectors = [
        '[data-testid="header-insights-button"]',
        'button[href="/insights"]',
        '.awsui_button[href="/insights"]',
        'a[href="/insights"]',
        '[aria-label*="dashboard"]',
        '[title*="dashboard"]',
      ];
    } else if (selector.includes("notifications-dropdown")) {
      possibleSelectors = [
        '[data-testid="header-notifications"]',
        ".my-notif-dropdown",
        'button[aria-label*="Notificaciones"]',
        'button[title*="Notificaciones"]',
        '[class*="notification"]',
        'button[aria-haspopup="menu"]',
      ];
    } else if (selector.includes("user-menu")) {
      possibleSelectors = [
        '[data-testid="header-user-menu"]',
        'button[aria-label*="user-profile"]',
        'button[title*="user"]',
        '[class*="user-profile"]',
        'button[aria-haspopup="menu"]:last-child',
      ];
    }

    const element = findElement(possibleSelectors);

    if (element) {
      document.querySelectorAll(".tutorial-highlight").forEach((el) => {
        el.classList.remove("tutorial-highlight");
      });

      element.classList.add("tutorial-highlight");

      element.scrollIntoView({
        behavior: "smooth",
        block: "center",
        inline: "center",
      });

      setHighlightedElement({ element, position });

      if (position === "center") {
        setModalPosition({
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
        });
      } else {
        setTimeout(() => {
          calculateModalPosition(element, position);
        }, 150);
      }
    } else {
      setHighlightedElement(null);
      setModalPosition({
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
      });
    }
  }, []);

  const calculateModalPosition = useCallback((element, position) => {
    if (!element) return;

    const rect = element.getBoundingClientRect();
    const modalPadding = 20;
    const modalWidth = 400;
    const modalHeight = 300;

    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    let newPosition = {};

    switch (position) {
      case "center":
        newPosition = {
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
        };
        break;

      case "bottom":
        let topPos = rect.bottom + modalPadding;

        if (topPos + modalHeight > viewportHeight) {
          topPos = rect.top - modalHeight - modalPadding;
        }

        let leftPos = rect.left + rect.width / 2;

        if (leftPos - modalWidth / 2 < modalPadding) {
          leftPos = modalPadding + modalWidth / 2;
        } else if (leftPos + modalWidth / 2 > viewportWidth - modalPadding) {
          leftPos = viewportWidth - modalPadding - modalWidth / 2;
        }

        newPosition = {
          top: `${Math.max(modalPadding, topPos)}px`,
          left: `${leftPos}px`,
          transform: "translateX(-50%)",
        };
        break;

      case "top":
        let bottomPos = viewportHeight - rect.top + modalPadding;

        if (rect.top - modalHeight < modalPadding) {
          bottomPos = viewportHeight - rect.bottom - modalHeight - modalPadding;
        }

        let leftPosTop = rect.left + rect.width / 2;

        if (leftPosTop - modalWidth / 2 < modalPadding) {
          leftPosTop = modalPadding + modalWidth / 2;
        } else if (leftPosTop + modalWidth / 2 > viewportWidth - modalPadding) {
          leftPosTop = viewportWidth - modalPadding - modalWidth / 2;
        }

        newPosition = {
          bottom: `${Math.max(modalPadding, bottomPos)}px`,
          left: `${leftPosTop}px`,
          transform: "translateX(-50%)",
        };
        break;

      case "left":
        let rightPos = viewportWidth - rect.left + modalPadding;

        if (rect.left - modalWidth < modalPadding) {
          rightPos = viewportWidth - rect.right - modalWidth - modalPadding;
        }

        newPosition = {
          top: `${rect.top + rect.height / 2}px`,
          right: `${Math.max(modalPadding, rightPos)}px`,
          transform: "translateY(-50%)",
        };
        break;

      case "right":
        let leftPosRight = rect.right + modalPadding;

        if (leftPosRight + modalWidth > viewportWidth - modalPadding) {
          leftPosRight = rect.left - modalWidth - modalPadding;
        }

        newPosition = {
          top: `${rect.top + rect.height / 2}px`,
          left: `${Math.max(modalPadding, leftPosRight)}px`,
          transform: "translateY(-50%)",
        };
        break;

      default:
        newPosition = {
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
        };
    }

    setModalPosition(newPosition);
  }, []);

  useEffect(() => {
    if (!showTutorial || !steps[currentStep]) return;

    const step = steps[currentStep];

    if (step.showCondition && !step.showCondition()) {
      if (currentStep < steps.length - 1) {
        setTimeout(() => setCurrentStep((prev) => prev + 1), 300);
      }
      return;
    }

    const timeoutId = setTimeout(() => {
      if (step.selector) {
        highlightElement(step.selector, step.position);
      } else {
        setHighlightedElement(null);
        setModalPosition({
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
        });
      }
    }, 500);

    return () => {
      clearTimeout(timeoutId);
      document.querySelectorAll(".tutorial-highlight").forEach((el) => {
        el.classList.remove("tutorial-highlight");
      });
    };
  }, [currentStep, showTutorial, steps, highlightElement]);

  useEffect(() => {
    const handleResize = () => {
      if (highlightedElement && steps[currentStep]) {
        calculateModalPosition(
          highlightedElement.element,
          highlightedElement.position
        );
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [highlightedElement, currentStep, steps, calculateModalPosition]);

  const handleClose = useCallback(() => {
    setShowTutorial(false);
    setHighlightedElement(null);
    setModalPosition({});

    document.querySelectorAll(".tutorial-highlight").forEach((el) => {
      el.classList.remove("tutorial-highlight");
    });

    if (!forceShow) {
      localStorage.setItem(storageKey, "true");
    }

    if (onComplete) {
      onComplete();
    }
  }, [onComplete, forceShow, storageKey]);

  const handleNext = useCallback(() => {
    if (currentStep < steps.length - 1) {
      setCurrentStep((prev) => prev + 1);
    } else {
      handleClose();
    }
  }, [currentStep, steps.length, handleClose]);

  const handlePrev = useCallback(() => {
    setCurrentStep((prev) => Math.max(0, prev - 1));
  }, []);

  const startTutorial = useCallback(() => {
    setShowTutorial(true);
    setCurrentStep(0);
  }, []);

  if (!steps.length) return null;

  const currentStepData = steps[currentStep];

  return (
    <>
      <style jsx global>{`
        .tutorial-highlight {
          position: relative !important;
          z-index: 1001 !important;
          box-shadow: 0 0 0 4px #0972d3, 0 0 0 8px rgba(9, 114, 211, 0.2) !important;
          border-radius: 8px !important;
          animation: tutorialPulse 2s infinite;
        }

        @keyframes tutorialPulse {
          0% {
            box-shadow: 0 0 0 4px #0972d3, 0 0 0 8px rgba(9, 114, 211, 0.2);
          }
          50% {
            box-shadow: 0 0 0 4px #0972d3, 0 0 0 12px rgba(9, 114, 211, 0.4);
          }
          100% {
            box-shadow: 0 0 0 4px #0972d3, 0 0 0 8px rgba(9, 114, 211, 0.2);
          }
        }

        .tutorial-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.4);
          z-index: 1000;
          pointer-events: none;
        }

        .tutorial-modal {
          position: fixed;
          z-index: 1002;
          max-width: 420px;
          min-width: 320px;
          background: white;
          border-radius: 12px;
          box-shadow: 0 12px 40px rgba(0, 0, 0, 0.15);
          pointer-events: auto;
        }

        @media (max-width: 768px) {
          .tutorial-modal {
            max-width: 90vw;
            min-width: 280px;
            margin: 10px;
          }
        }

        @media (max-width: 480px) {
          .tutorial-modal {
            max-width: 95vw;
            min-width: 260px;
            margin: 5px;
          }
        }
      `}</style>

      {triggerButton && (
        <div
          onClick={startTutorial}
          title={t("tutorial.startGuide", "Ver guía del tutorial")}
          style={{
            position: "fixed",
            top: 80,
            right: 16,
            zIndex: 999,
            backgroundColor: "rgba(9, 114, 211, 0.8)",
            color: "white",
            border: "none",
            borderRadius: "50%",
            width: "28px",
            height: "28px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            fontSize: "14px",
            fontWeight: "bold",
            fontFamily: "Arial, sans-serif",
            boxShadow: "0 2px 6px rgba(0,0,0,0.15)",
            transition: "all 0.2s ease",
            opacity: "0.6",
          }}
          onMouseEnter={(e) => {
            e.target.style.opacity = "1";
            e.target.style.transform = "scale(1.15)";
            e.target.style.backgroundColor = "rgba(9, 114, 211, 1)";
          }}
          onMouseLeave={(e) => {
            e.target.style.opacity = "0.6";
            e.target.style.transform = "scale(1)";
            e.target.style.backgroundColor = "rgba(9, 114, 211, 0.8)";
          }}
        >
          ?
        </div>
      )}

      {showTutorial && <div className="tutorial-overlay" />}

      {showTutorial && currentStepData && (
        <div className="tutorial-modal" style={modalPosition}>
          <Container
            header={
              <Header
                variant="h3"
                actions={
                  <Button
                    variant="icon"
                    onClick={handleClose}
                    iconName="close"
                    ariaLabel={t("tutorial.close", "Cerrar tutorial")}
                  />
                }
              >
                {currentStepData.title}
              </Header>
            }
          >
            <SpaceBetween size="m">
              <Box>{currentStepData.content}</Box>

              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  gap: "6px",
                  padding: "8px 0",
                }}
              >
                {steps.map((_, index) => (
                  <div
                    key={index}
                    style={{
                      width: "10px",
                      height: "10px",
                      borderRadius: "50%",
                      backgroundColor:
                        index === currentStep ? "#0972d3" : "#d5dbdb",
                      transition: "all 0.3s ease",
                      transform:
                        index === currentStep ? "scale(1.2)" : "scale(1)",
                      cursor: "pointer",
                    }}
                    onClick={() => setCurrentStep(index)}
                    title={`Ir al paso ${index + 1}`}
                  />
                ))}
              </div>

              <SpaceBetween direction="horizontal" size="xs">
                <Button
                  disabled={currentStep === 0}
                  onClick={handlePrev}
                  iconName="angle-left"
                  variant="link"
                >
                  {t("tutorial.previous", "Anterior")}
                </Button>

                <div
                  style={{
                    flex: 1,
                    textAlign: "center",
                    alignSelf: "center",
                    fontSize: "14px",
                    color: "#5f6b7a",
                    fontWeight: 500,
                  }}
                >
                  {currentStep + 1} {t("tutorial.of", "de")} {steps.length}
                </div>

                <Button
                  variant="primary"
                  onClick={handleNext}
                  iconName={
                    currentStep === steps.length - 1
                      ? "status-positive"
                      : "angle-right"
                  }
                  iconAlign={
                    currentStep === steps.length - 1 ? "left" : "right"
                  }
                >
                  {currentStep === steps.length - 1
                    ? t("tutorial.finish", "Finalizar")
                    : t("tutorial.next", "Siguiente")}
                </Button>
              </SpaceBetween>
            </SpaceBetween>
          </Container>
        </div>
      )}
    </>
  );
};

export default TutorialGuide;
